import { useState, useEffect, useRef } from "react";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerIcon2 from "leaflet/dist/images/marker-icon.png"; // reuse for color simulation

import { db } from "./firebase";
import { doc, setDoc, getDoc, onSnapshot, collection } from "firebase/firestore";

function App() {

  // 🔵 Marker styles (simulate different users)
  const icons = [
  new L.Icon({ iconUrl: markerIcon, iconSize: [25, 41] }), // default
  new L.Icon({ iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png", iconSize: [25, 41] }),
  new L.Icon({ iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-green.png", iconSize: [25, 41] }),
  new L.Icon({ iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-orange.png", iconSize: [25, 41] }),
  new L.Icon({ iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-violet.png", iconSize: [25, 41] }),
];

  const [position, setPosition] = useState(null);
  const [users, setUsers] = useState([]);
  const [roomCode, setRoomCode] = useState("");
  const [inputCode, setInputCode] = useState("");
  const [joined, setJoined] = useState(false);

  const prevUsersRef = useRef([]);

  // ✅ Unique user
  const [userId] = useState(() => {
    let id = localStorage.getItem("userId");
    if (!id) {
      id = "user_" + Date.now() + "_" + Math.random().toString(36).substring(2, 6);
      localStorage.setItem("userId", id);
    }
    return id;
  });

  // Load & save room
  useEffect(() => {
    const saved = localStorage.getItem("roomCode");
    if (saved) {
      setRoomCode(saved);
      setJoined(true);
    }
  }, []);

  useEffect(() => {
    if (roomCode) localStorage.setItem("roomCode", roomCode);
  }, [roomCode]);

  // Create
  const createTrip = async () => {
    const code = Math.random().toString(36).substring(2, 7).toUpperCase();
    await setDoc(doc(db, "rooms", code), { createdAt: Date.now() });
    setRoomCode(code);
    setJoined(true);
  };

  // Join
  const joinTrip = async () => {
    const roomSnap = await getDoc(doc(db, "rooms", inputCode));
    if (roomSnap.exists()) {
      setRoomCode(inputCode);
      setJoined(true);
    } else {
      alert("Invalid Code ❌");
    }
  };

  // 📍 Location + Heartbeat
  useEffect(() => {
    if (!joined || !roomCode) return;

    let lat = null, lng = null;

    const watchId = navigator.geolocation.watchPosition(
      (pos) => {
        lat = pos.coords.latitude;
        lng = pos.coords.longitude;

        setPosition([lat, lng]);

        setDoc(doc(db, "rooms", roomCode, "users", userId), {
          lat, lng,
          updatedAt: Date.now()
        });
      },
      console.log,
      { enableHighAccuracy: true }
    );

    const interval = setInterval(() => {
      if (lat && lng) {
        setDoc(doc(db, "rooms", roomCode, "users", userId), {
          lat, lng,
          updatedAt: Date.now()
        });
      }
    }, 5000);

    return () => {
      navigator.geolocation.clearWatch(watchId);
      clearInterval(interval);
    };
  }, [joined, roomCode, userId]);

  // 🔄 Listen + detect leave
  useEffect(() => {
    if (!joined || !roomCode) return;

    const unsub = onSnapshot(
      collection(db, "rooms", roomCode, "users"),
      (snapshot) => {

        const now = Date.now();

        const active = snapshot.docs
          .map(doc => ({ id: doc.id, ...doc.data() }))
          .filter(u => now - u.updatedAt < 20000);

        // 🔔 Detect leaving users
        const prev = prevUsersRef.current;
        prev.forEach(p => {
          if (!active.find(a => a.id === p.id)) {
            alert("User left ❌");
          }
        });

        prevUsersRef.current = active;
        setUsers(active);
      }
    );

    return () => unsub();
  }, [joined, roomCode]);

  // 📏 Distance calc
  function getDistance(lat1, lon1, lat2, lon2) {
    const R = 6371;
    const dLat = (lat2 - lat1) * Math.PI/180;
    const dLon = (lon2 - lon1) * Math.PI/180;

    const a = Math.sin(dLat/2)**2 +
      Math.cos(lat1*Math.PI/180) *
      Math.cos(lat2*Math.PI/180) *
      Math.sin(dLon/2)**2;

    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  }

  return (
    <div style={{ textAlign: "center" }}>
      <h1>Live Tracker</h1>

      {!joined ? (
        <>
          <button onClick={createTrip}>Create</button><br/><br/>
          <input value={inputCode} onChange={e=>setInputCode(e.target.value)} />
          <button onClick={joinTrip}>Join</button>
        </>
      ) : (
        <>
          <h2>Room: {roomCode}</h2>

          {/* 📏 Distance Panel */}
          <div style={{
            position:"absolute", top:10, left:10,
            background:"white", padding:10, zIndex:1000
          }}>
            <h4>Distances</h4>
            {position && users.map((u,i)=>(
              <div key={i}>
                User {i+1}: {(getDistance(position[0], position[1], u.lat, u.lng)*1000).toFixed(0)} m
              </div>
            ))}
          </div>

          {position && (
            <MapContainer center={position} zoom={15} style={{ height:"500px" }}>
              <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"/>

              {users.map((u, i) => {
  const colorIndex = u.id
    ? u.id.charCodeAt(0) % icons.length
    : i % icons.length;

  return (
    <Marker
      key={u.id || i}
      position={[u.lat, u.lng]}
      icon={icons[colorIndex]}
    >
      <Popup>
        User {i + 1} <br />
        Last seen: {Math.floor((Date.now() - u.updatedAt) / 1000)} sec ago
      </Popup>
    </Marker>
  );
})}

            </MapContainer>
          )}
        </> 
      )}
    </div>
  );
}

export default App;